import { useQuery, UseQueryResult } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import {
  CustomFieldRecord,
  CustomFieldStatus,
  TargetEntityType,
} from "@/shared/domain/customFields";

import { customFieldQueryKeys } from "./customFieldQueryKeys";

// Ready custom-field definitions change rarely and only via admin action, so
// polling isn't worth it. A short stale time instead refreshes a grid's
// available filters/columns on the next window-focus or navigation.
const READY_FIELDS_STALE_TIME_MS = 60_000;

export interface UseReadyCustomFieldsParams {
  entityType: TargetEntityType;
  /** Override the resolved org; defaults to the current org. */
  orgId?: string;
  /** Gate the query (e.g. behind the release-custom-fields feature flag). */
  enabled?: boolean;
}

/**
 * Lists the Ready custom fields for an entity type in the current org, returning
 * just the field records (the Ready set is one page, <=15 rows). Unlike
 * `useCustomFields`, it requests only Ready fields, caches them separately from
 * the Settings all-statuses list, and does not poll.
 */
export function useReadyCustomFields({
  entityType,
  orgId,
  enabled = true,
}: UseReadyCustomFieldsParams): UseQueryResult<CustomFieldRecord[], Error> {
  const { customFieldService } = useDomainServices();
  const { currentOrganization } = useAuth();
  const canonicalOrgId = orgId || currentOrganization?.org_id;

  return useQuery({
    queryKey: customFieldQueryKeys.readyList(canonicalOrgId ?? "", entityType),
    queryFn: ({ signal }) => {
      if (canonicalOrgId === undefined) {
        return Promise.reject(new Error("Org ID must be provided!"));
      }

      return customFieldService
        .listCustomFields(
          { entity_type: entityType, statuses: [CustomFieldStatus.Ready] },
          { abortSignal: signal, orgId: canonicalOrgId },
        )
        .then((page) => page.items);
    },
    enabled: enabled && canonicalOrgId !== undefined,
    staleTime: READY_FIELDS_STALE_TIME_MS,
  });
}
